import React from 'react';
import { Box, Tooltip, Typography } from '@mui/material';
import { ChevronRight } from '@mui/icons-material';
import { STAGES, getStage, pct } from '../../utils/platformKpi';
import { Section, fmtRate } from './common';

const DAY = 86400000;
const STAGE_COLORS = ['#B0B7C3', '#FF9830', '#FADE2A', '#5794F2', '#73BF69'];

const median = (values) => {
  if (!values.length) return null;
  const s = [...values].sort((a, b) => a - b);
  const mid = Math.floor(s.length / 2);
  return s.length % 2 ? s[mid] : Math.round((s[mid - 1] + s[mid]) / 2);
};

/** Usage ladder funnel — is each center really using Thaklis? (design §3.3) */
const UsageLadder = ({ centers, source, onSelectStage }) => {
  const counts = Object.fromEntries(STAGES.map((s) => [s.key, 0]));
  centers.forEach((c) => { counts[getStage(c)] += 1; });

  const daysToInvoice = centers
    .filter((c) => c.createdAt && c.firstInvoiceAt)
    .map((c) => Math.max(0, Math.round((new Date(c.firstInvoiceAt) - new Date(c.createdAt)) / DAY)));
  const activatedIn30 = centers.filter((c) => c.createdAt && c.firstWorkOrderAt && c.firstInvoiceAt
    && (new Date(c.firstInvoiceAt) - new Date(c.createdAt)) / DAY <= 30).length;
  const medianDays = median(daysToInvoice);

  return (
    <Section title="Usage ladder" source={source}>
      <Box sx={{ display: 'flex', alignItems: 'stretch', gap: 0.5, flexWrap: { xs: 'wrap', md: 'nowrap' } }}>
        {STAGES.map((s, i) => (
          <React.Fragment key={s.key}>
            <Tooltip title={s.hint}>
              <Box
                data-testid={`stage-${s.key}`}
                onClick={() => onSelectStage(s.key)}
                sx={{
                  flex: '1 1 0',
                  minWidth: 96,
                  p: 1.25,
                  borderRadius: 1,
                  cursor: 'pointer',
                  borderTop: `3px solid ${STAGE_COLORS[i]}`,
                  bgcolor: 'action.hover',
                  '&:hover': { bgcolor: 'action.selected' },
                }}
              >
                <Typography sx={{ fontSize: '0.75rem', color: 'text.secondary' }}>{i + 1}. {s.label}</Typography>
                <Typography data-testid={`stage-${s.key}-count`} sx={{ fontSize: '1.375rem', fontWeight: 700, lineHeight: 1.3 }}>
                  {counts[s.key]}
                </Typography>
                <Typography sx={{ fontSize: '0.7rem', color: 'text.secondary' }}>
                  {pct(counts[s.key], centers.length) ?? 0}% of centers
                </Typography>
              </Box>
            </Tooltip>
            {i < STAGES.length - 1 && (
              <Box sx={{ display: { xs: 'none', md: 'flex' }, alignItems: 'center', color: 'text.disabled' }}>
                <ChevronRight fontSize="small" />
              </Box>
            )}
          </React.Fragment>
        ))}
      </Box>
      <Box sx={{ display: 'flex', gap: 3, mt: 1.5, flexWrap: 'wrap' }}>
        <Typography sx={{ fontSize: '0.8125rem' }}>
          Activation (started within 30 days):{' '}
          <Box component="span" data-testid="activation-rate" sx={{ fontWeight: 600 }}>{fmtRate(activatedIn30, centers.length)}</Box>
        </Typography>
        <Typography sx={{ fontSize: '0.8125rem' }}>
          Median days to first invoice:{' '}
          <Box component="span" sx={{ fontWeight: 600 }}>{medianDays ?? '—'}</Box>
        </Typography>
      </Box>
    </Section>
  );
};

export default UsageLadder;
